import { useEffect, useState, type ReactNode } from 'react'
import { WifiOff } from 'lucide-react'
import type { LiveConversationDetail } from '@manaro/contracts'
import { useSession } from '@/features/auth/use-session.ts'
import { ChatHeader } from './chat-header.tsx'
import { Composer } from './composer.tsx'
import { MessageList } from './message-list.tsx'

interface ChatScreenProps {
  detail: LiveConversationDetail
  /** Polling failed but the last state is still on screen: keep the chat, say the connection is gone. */
  offline?: boolean
  /** Phones: a back arrow to the list. Two-pane layouts leave it out. */
  onBack?: () => void
}

interface Pending {
  text: string
  /** The last message id when it was sent: the echo from the server comes after it. */
  afterId: number
}

export function ChatScreen({ detail, offline = false, onBack }: ChatScreenProps) {
  const me = useSession().data?.user.email ?? ''
  const [pending, setPending] = useState<Pending | null>(null)
  const { conversation, messages } = detail
  const lastId = messages.at(-1)?.id ?? 0

  // The optimistic bubble goes away exactly when the real message shows up — no gap, no double.
  useEffect(() => {
    if (pending && messages.some((message) => message.id > pending.afterId && message.role === 'operator' && message.text === pending.text)) {
      setPending(null)
    }
  }, [messages, pending])

  return (
    <div className="flex h-full min-h-0 flex-col bg-canvas">
      <ChatHeader item={conversation} onBack={onBack} />
      {offline && (
        <p role="status" className="flex shrink-0 items-center gap-2 bg-warn-tint px-4 py-1.5 text-[13px] text-warn-ink">
          <WifiOff className="size-4 shrink-0" aria-hidden />
          Нет связи с сервером — переподключаемся…
        </p>
      )}
      {/* Sibling keys must differ: with one shared key React lost track of the list and stacked a stale copy on every update. */}
      <MessageList
        key={`messages:${conversation.id}`}
        messages={messages}
        botTyping={conversation.botTyping}
        pendingText={pending?.text ?? null}
      />
      <Composer
        key={`composer:${conversation.id}`}
        conversation={conversation}
        me={me}
        onSending={(text) => setPending(text === null ? null : { text, afterId: lastId })}
      />
    </div>
  )
}

/** On phones the conversation covers everything, tab bar included, and follows the keyboard on iOS. */
export function FullScreenLayer({ children }: { children: ReactNode }) {
  useEffect(() => {
    const { overflow } = document.body.style
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = overflow
    }
  }, [])

  return (
    <div className="fixed inset-x-0 top-[var(--app-top,0px)] z-40 h-[var(--app-height,100dvh)] bg-canvas">{children}</div>
  )
}
